import { useState } from "react";

/**
 * Maps currency codes to their corresponding symbols
 * @param {string} currencyCode - The ISO currency code (e.g., 'USD', 'EUR')
 * @returns {string} The currency symbol (e.g., '$', '€')
 */
export const getCurrencySymbol = (currencyCode) => {
  const currencyMap = {
    USD: "$", // US Dollar
    EUR: "€", // Euro
    GBP: "£", // British Pound
    JPY: "¥", // Japanese Yen
    INR: "₹", // Indian Rupee
    CNY: "¥", // Chinese Yuan
    AUD: "A$", // Australian Dollar
    CAD: "C$", // Canadian Dollar
    CHF: "CHF", // Swiss Franc
    KRW: "₩", // South Korean Won
    MXN: "MX$", // Mexican Peso
    BRL: "R$", // Brazilian Real
    ARS: "$", // Argentine Peso
    CLP: "$", // Chilean Peso
    CRC: "₡", // Costa Rican Colón
    IDR: "Rp", // Indonesian Rupiah
    PKR: "₨", // Pakistani Rupee
    SZL: "E", // Swazi Lilangeni
  };
  return currencyMap[currencyCode] || currencyCode || "$";
};

/**
 * Maps currency codes to their full names
 * @param {string} currencyCode - The ISO currency code (e.g., 'USD', 'EUR')
 * @returns {string} The currency full name (e.g., 'US Dollar', 'Euro')
 */
export const getCurrencyFullName = (currencyCode) => {
  const currencyNameMap = {
    USD: "US Dollar",
    EUR: "Euro",
    GBP: "British Pound",
    JPY: "Japanese Yen",
    INR: "Indian Rupee",
    CNY: "Chinese Yuan",
    AUD: "Australian Dollar",
    CAD: "Canadian Dollar",
    CHF: "Swiss Franc",
    KRW: "South Korean Won",
    MXN: "Mexican Peso",
    BRL: "Brazilian Real",
    ARS: "Argentine Peso",
    CLP: "Chilean Peso",
    CRC: "Costa Rican Colón",
    IDR: "Indonesian Rupiah",
    PKR: "Pakistani Rupee",
    SZL: "Swazi Lilangeni",
  };
  return currencyNameMap[currencyCode] || currencyCode;
};

/**
 * React component that displays currency amounts in abbreviated format
 * Shows full value with commas on hover with smooth transition
 * @param {Object} props
 * @param {number|string} props.value - The amount to display
 * @param {string} props.currency - The currency code
 */
export const AmountCell = ({ value, currency }) => {
  const [isHovered, setIsHovered] = useState(false);
  const symbol = getCurrencySymbol(currency);

  if (value == null || value === "") return "";

  const numAmount = parseFloat(value);
  if (isNaN(numAmount)) return value;

  // Format abbreviated value
  let abbreviated;
  if (Math.abs(numAmount) >= 1000000) {
    abbreviated = `${symbol}${(numAmount / 1000000).toFixed(2)}M`;
  } else if (Math.abs(numAmount) >= 1000) {
    abbreviated = `${symbol}${(numAmount / 1000).toFixed(2)}K`;
  } else {
    abbreviated = `${symbol}${numAmount.toFixed(2)}`;
  }

  // Full value with commas
  const fullValue = `${symbol}${numAmount.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        cursor: "pointer",
        color: "#1976d2",
        fontWeight: 500,
        textDecoration: "underline",
        textDecorationStyle: "dotted",
        textUnderlineOffset: "3px",
        fontSize: isHovered ? "1.6em" : "inherit",
        transition: "font-size 0.2s ease",
      }}
    >
      {isHovered ? fullValue : abbreviated}
    </div>
  );
};

/**
 * Cell renderer function for AG Grid or similar table components
 * @param {Object} params - Cell renderer parameters from AG Grid
 * @returns {JSX.Element} AmountCell component
 */
export const renderAmountCell = (params) => {
  return <AmountCell value={params.value} currency={params.data.currency} />;
};

export const formatValue = (value) => {
  const num = Number(value);

  if (!Number.isFinite(num)) return value; // Return as-is if it's not a number

  // format with 2 decimal places and commas
  return num.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};
